import { useState, useEffect, useRef } from "react"
import modals from "src/components/modals"
import { playUiSound } from "src/utils/audio"

function shallowEqual(obj1, obj2) {
  if (obj1 === obj2) return true
  if (!obj1 || !obj2) return false
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  if (keys1.length !== keys2.length) return false
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) return false
  }
  return true
}

class ModalService {
  static instance = null
  listener = null
  // Told the type and props of every `show`, for whoever needs to know a modal was asked for
  // without being the host that draws it — basic training's battle step is waiting for the battle
  // view to be opened, by whichever of its doors the player finds.
  observers = new Set()

  static getInstance() {
    if (!ModalService.instance) {
      ModalService.instance = new ModalService()
      ModalService.instance.initEscapeListener()
    }
    return ModalService.instance
  }

  show(type, props = {}) {
    /* Here rather than at each of the forty callers. Outside the updater, not inside it: React
       runs a state updater twice under strict mode, and a doubled blip is audible. The cost is
       that the duplicate `show` the check below swallows still makes a sound — which happens when
       something asks for a modal already on top, rarely, and sounds like the click that asked. */
    playUiSound("open")
    this.observers.forEach((observer) => observer(type, props))
    if (this.listener) {
      this.listener((prevModals) => {
        const lastModal = prevModals[prevModals.length - 1]

        // Avoid adding duplicate if the last modal is the same type and props
        const isDuplicate = lastModal && lastModal.type === type && shallowEqual(lastModal.props, props)

        if (isDuplicate) {
          return prevModals // no-op
        }

        // The id is what keeps a modal *the same* modal to React: entries below the top move
        // around as the stack grows and shrinks, and a key made from the position would remount
        // — and reset — a modal nobody touched. Derived from the stack rather than a counter on
        // the service, so the updater stays a pure function of its argument.
        const id = prevModals.reduce((highest, modal) => Math.max(highest, modal.id ?? 0), 0) + 1
        return [...prevModals, { id, type, props }]
      })
    }
  }

  /**
   * Hides the most recent modal.
   * @param {string} [modalType] - If provided, hides the most recent modal of that specific type.
   * Otherwise, it hides the last modal in the stack.
   */
  hide(modalType) {
    if (this.listener) {
      this.listener((prevModals) => {
        // If no type is specified, revert to original behavior: remove the last modal.
        if (!modalType) {
          return prevModals.slice(0, -1)
        }

        const indexToRemove = prevModals.findLastIndex((modal) => modal.type === modalType)
        if (indexToRemove === -1) {
          return prevModals
        }
        return [...prevModals.slice(0, indexToRemove), ...prevModals.slice(indexToRemove + 1)]
      })
    }
  }

  /**
   * Hides all modals.
   * @param {string} [modalType] - If provided, hides all modals of that specific type.
   * Otherwise, it clears the entire modal stack.
   */
  hideAll(modalType) {
    if (this.listener) {
      this.listener((prevModals) => {
        if (!modalType) {
          return []
        }
        return prevModals.filter((modal) => modal.type !== modalType)
      })
    }
  }

  subscribe(listener) {
    this.listener = listener
  }

  unsubscribe() {
    this.listener = null
  }

  /** Calls `observer(type, props)` on every `show`; returns the function that stops it. */
  onShow(observer) {
    this.observers.add(observer)
    return () => this.observers.delete(observer)
  }

  /** Whether any modal is on screen. Kept current by ModalHost. */
  hasOpenModals() {
    return (this.openCount ?? 0) > 0
  }

  /** Whether a modal of this type is in the stack. Kept current by ModalHost. */
  isShowing(modalType) {
    return (this.stackTypes ?? []).includes(modalType)
  }

  /* Escape closes the top modal and nothing else (owner, 2026-09-30: with the city sheet over the
     action bar, Escape "closes both"). Two listeners heard the one press — this one, and the Game
     view's, which backs out of the map's own modes — and the view's asked `hasOpenModals()`, which
     reads the stack as it was last rendered. From a real keyboard the browser runs React's
     microtasks between one listener and the next, so the stack had already re-rendered empty by
     the time the view asked, and the cell was deselected under the sheet that had just closed.

     So the answer is carried by the event rather than read off the stack afterwards: a press that
     closed a modal is remembered here, and `closedAModal(event)` is what every other Escape
     handler asks first. This listener is added when the service is first imported, before any
     component mounts, so it hears a press before any other window listener in the bubble phase;
     the ones that must go first (the shortcut recorder, the spawn tour) listen in the capture
     phase and stop the press themselves. */
  escapesThatClosed = new WeakSet()

  initEscapeListener() {
    window.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return
      if (this.hasOpenModals()) this.escapesThatClosed.add(e)
      this.hide()
    })
  }

  /** Whether this keydown was the Escape that closed the top modal — if so, it is spent. */
  closedAModal(event) {
    return this.escapesThatClosed.has(event)
  }
}

const modalServiceInstance = ModalService.getInstance()

const ModalHost = () => {
  const [modalData, setModalData] = useState([])
  const prevModalsRef = useRef([])

  useEffect(() => {
    modalServiceInstance.subscribe(setModalData)
    return () => modalServiceInstance.unsubscribe()
  }, [])

  // Mirrored onto the service so non-React callers (the Game view's Escape handling) can tell
  // whether a modal is on screen without subscribing.
  modalServiceInstance.openCount = modalData.length
  modalServiceInstance.stackTypes = modalData.map((m) => m.type)

  useEffect(() => {
    const prev = prevModalsRef.current
    const curr = modalData
    const removed = prev.filter((mPrev) => !curr.includes(mPrev))
    removed.forEach((m) => m?.props?.onClose?.())
    prevModalsRef.current = curr
  }, [modalData])

  if (modalData.length === 0) return null

  // Every modal in the stack stays mounted and only the top one is on screen. Rendering the top
  // one alone unmounted whatever it covered, so closing it handed back a fresh copy: open a
  // unit's stats from a battle you were reading three rounds back and you returned to the live
  // round, at the last sub-round, scrolled to the top. `visibility` and not `display: none`,
  // because that destroys the layout box and with it every scroll offset inside — which is half
  // of what this exists to keep.
  return modalData.map(({ id, type, props }, index) => {
    const Component = modals[type]
    if (!Component) return null
    const isTop = index === modalData.length - 1
    return (
      <div
        key={id ?? index}
        className={`modal-layer${isTop ? "" : " modal-layer-hidden"}`}
        aria-hidden={isTop ? undefined : "true"}
      >
        <Component {...props} />
      </div>
    )
  })
}

export { ModalHost }
export default ModalService.getInstance()
